import { CHANNELS, createBroadcastChannel } from '../platform/events/broadcast';
import { subscribe, unsubscribeAll } from '../platform/events/lifecycle';
import { emitLiveAgentAnalytics } from './analytics';
import { MessageType } from '../shared/message';
import { logger } from '../shared/utils/logger';
import {
  initializeSprinklrChat,
  destroySprinklrChat,
} from '../platform/transport/sprinklr-chat-service';
import {
  startConversation,
  endConversation,
  getMessages,
  sendMessage,
  sendMessageWithImage,
  onMessageReceived,
} from '../platform/transport/sprinklr-conversation';
import {
  LIVE_AGENT_ANALYTICS_CONNECTION_STRING,
  LIVE_AGENT_ANALYTICS_DISCONNECTION_STRING,
  LIVE_AGENT_ANALYTICS_CUSTOM_MESSAGE_PREFIX,
  LIVE_AGENT_ANALYTICS_FIRST_MESSAGE_SUFFIX,
  TYPING_TTL_MS,
} from './constants';

/**
 * The Sprinklr live-agent engine: a non-hook factory that reconciles escalation intent with the
 * live SDK. See .claude/context/live-agent.md "Engine and block design".
 *
 * @param {Object}   ports
 * @param {() => Object} ports.getContext  the live consumer context, read at FIRE time —
 *   `{ enabled, idleTTL, store, sessionId, onAgentMessage, onConversationEnd,
 *      onSurveyReceived, onImageReceived, onIdleDisconnect, setEscalated }`.
 * @param {Object}   ports.persistence  the `maa-live-agent` record (this block is its sole
 *   WRITER; Shell hooks cross-read it).
 * @param {() => Object} ports.persistence.read   current record — must be the SYNCHRONOUS
 *   authority, not a render-time snapshot (see "Persistence write path" in live-agent.md).
 * @param {(record: Object) => void} ports.persistence.write  replace the record.
 * @returns {Object} `init` / `dispose` plus the capability surface the façade re-exports.
 */
export const createSprinklrEngine = ({ getContext, persistence }) => {
  const seed = persistence.read() || {};

  // Observed fact, not the persisted intent (`isEscalated`); not part of the returned surface.
  // See .claude/context/live-agent.md
  let isAgentConnected = false;
  let conversationId = seed.conversationId || null;
  let caseNumber = seed.caseNumber ?? null;
  let unsubscribeMessages = null;
  let unsubscribeBus = null;
  let surveyTimeout = null;
  let typingTimeout = null;
  let channel = null;
  let hasFirstMessageBeenSent = false;

  // Kept after `conversationId` is cleared so disconnect/survey analytics can still name it.
  // Read via getConversationIds(), not `conversationId`. See .claude/context/live-agent.md
  let lastConversationId = seed.conversationId || null;
  let lastCaseNumber = seed.caseNumber ?? null;
  const getConversationIds = () => ({
    conversationId: lastConversationId,
    caseNumber: lastCaseNumber,
  });

  const setAgentConnected = (value) => {
    isAgentConnected = value;
    const { store } = getContext();
    if (store) store.getState().setIsAgentConnected(value);
  };

  const setAgentTyping = (value) => {
    const { store } = getContext();
    if (store) store.getState().setIsAgentTyping(value);
  };

  const clearTypingTimeout = () => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
      typingTimeout = null;
    }
  };

  const stopAgentTyping = () => {
    clearTypingTimeout();
    setAgentTyping(false);
  };

  // Escalation is owned by the conversation store slice (the single source of truth); the
  // consumer passes `setEscalated` = the store action.
  const publishEscalation = (value) => {
    const { setEscalated } = getContext();
    if (setEscalated) setEscalated(value);
  };

  // Single write-path: merge onto persistence.read() fresh, not a stale render-scoped copy.
  // See .claude/context/live-agent.md
  const writeSession = (patch) => {
    persistence.write({ ...persistence.read(), ...patch });
  };

  const setEscalationActive = () => {
    publishEscalation(true);
    writeSession({
      isEscalated: true,
      conversationId,
      caseNumber,
      lastActivityAt: Date.now(),
    });
  };

  const clearEscalation = () => {
    publishEscalation(false);
    conversationId = null;
    caseNumber = null;
    writeSession({
      isEscalated: false, conversationId: null, caseNumber: null, lastActivityAt: null,
    });
  };

  // ------ Idle TTL check — disconnect if the user has been idle for > the resolved TTL.
  // A function rather than a pre-computed boolean so `Date.now()` is evaluated at call time.
  const checkIdleExpired = () => {
    const record = persistence.read() || {};
    const { idleTTL } = getContext();
    return !!(record.isEscalated
      && record.lastActivityAt
      && (Date.now() - record.lastActivityAt > idleTTL));
  };

  const updateLastActivity = () => {
    const now = Date.now();
    writeSession({ lastActivityAt: now });
    if (channel) {
      channel.postMessage({ type: 'MAA_UPDATE_LAST_ACTIVITY', lastActivityAt: now });
    }
  };

  /**
   * The one conversation-end path: stops delivery, destroys the SDK, clears escalation. It never
   * broadcasts or writes the "ended" message. See .claude/context/live-agent.md
   *
   * @param {object}  [opts]
   * @param {boolean} [opts.awaitSurvey]  keep the SDK + subscription alive so a survey
   *   MESSAGE_RECEIVED can still arrive (Sprinklr sends it after the close event, with no
   *   deterministic hand-off available yet — see the 30s fallback at the call site).
   */
  const endSession = ({ awaitSurvey = false } = {}) => {
    logger.log('[Sprinklr] endSession called');
    if (surveyTimeout) {
      clearTimeout(surveyTimeout);
      surveyTimeout = null;
    }
    if (!awaitSurvey) {
      if (unsubscribeMessages) {
        unsubscribeMessages();
        unsubscribeMessages = null;
      }
      destroySprinklrChat();
    }
    // Read BEFORE clearing — setAgentConnected resets the flag itself.
    const wasConnected = isAgentConnected;
    setAgentConnected(false);
    stopAgentTyping();
    if (wasConnected) {
      emitLiveAgentAnalytics({
        lvQuestion: LIVE_AGENT_ANALYTICS_DISCONNECTION_STRING,
        sessionId: getContext().sessionId,
        conversationId: lastConversationId,
        caseNumber: lastCaseNumber,
      });
    }
    clearEscalation();
  };

  /**
   * Handles `magic-apron-assistant.close-agent-conversation`: closes the CURRENT conversation.
   * Teardown and the survey follow the SDK close event. See .claude/context/live-agent.md
   */
  const closeAgentConversation = () => {
    endConversation(conversationId);
  };

  /**
   * Initialises the Sprinklr SDK, starts a conversation, and subscribes to incoming
   * agent messages.
   */
  const initiateAgentChat = async (initialMessage = { text: '' }, agentSeedMessages) => {
    // Inert for journeys that did not declare the live-agent block. Guarded here rather than
    // only in init() because the flows can call this directly.
    if (!getContext().enabled) return;

    // Retry policy lives inline (not a `[sprinklrError]` effect, which has no caller to reject
    // to). See .claude/context/live-agent.md
    const attempt = async () => {
      const { text } = initialMessage || {};

      if (unsubscribeMessages) {
        unsubscribeMessages();
        unsubscribeMessages = null;
      }

      logger.log('[Sprinklr] initiateAgentChat — starting SDK init…');

      // 1. Initialise the SDK (loads script, waits for READY)
      await initializeSprinklrChat();

      // 2. Start a conversation if there is no existing one (WebSocket is now open)
      if (!conversationId) {
        const initialMessages = [
          ...(agentSeedMessages || []).map((msg) => {
            const messageText = typeof (msg.content) === 'string'
              ? msg.content
              : msg.content?.responseSummary?.summaryAnswer;
            return {
              // Bot answers are Markdown; unflagged, the agent console shows the syntax raw.
              message: msg.type === 'bot' && messageText ? { text: messageText, markdownText: true } : messageText,
              isSentByUser: msg.type === 'user',
            };
          }),
          { message: text, isSentByUser: true },
        ].filter(({ message }) => message);

        try {
          // MAG4-856: hand the adapter only { sessionId }, not the whole record.
          // See .claude/context/live-agent.md
          const {
            conversationId: newConversationId,
            caseNumber: newCaseNumber,
          } = await startConversation(
            { initialMessages },
            { sessionId: getContext().sessionId },
            getContext().deviceType,
          );

          logger.log('[Sprinklr] Conversation started with ID:', newConversationId, 'caseNumber:', newCaseNumber);
          conversationId = newConversationId;
          caseNumber = newCaseNumber;
          lastConversationId = newConversationId;
          lastCaseNumber = newCaseNumber;
          writeSession({ conversationId: newConversationId, caseNumber: newCaseNumber });
          if (channel) {
            channel.postMessage({
              type: 'MAA_ESCALATE_TO_AGENT',
              conversationId: newConversationId,
              caseNumber: newCaseNumber,
            });
          }
        } catch (err) {
          logger.error('[sprinklrEngine] Failed to start conversation:', err);
          throw err;
        }
      }

      // 3. Subscribe to incoming messages — read the handlers through getContext() so the
      //    subscription always calls the consumer's LATEST versions.
      unsubscribeMessages = onMessageReceived({
        onNewMessage: async (incomingMessage) => {
          logger.log('[Sprinklr] Received message from SDK:', incomingMessage);
          stopAgentTyping();
          const { onAgentMessage } = getContext();
          if (incomingMessage.type === MessageType.AGENT && onAgentMessage) {
            return onAgentMessage(incomingMessage).catch(() => {});
          }
          return Promise.resolve();
        },
        onConversationEnd: () => {
          // Re-entry guard: a close can surface >1 CONVERSATIONS_UPDATED event.
          if (surveyTimeout) {
            return;
          }

          // Save the "ended" system message for both user- and agent-initiated closes.
          const { onConversationEnd } = getContext();
          if (onConversationEnd) {
            onConversationEnd({ messageId: conversationId });
          }
          // Tell peer tabs; the tab that DETECTS announces. See .claude/context/live-agent.md
          if (channel) {
            channel.postMessage({ type: 'MAA_AGENT_DISCONNECTED' });
          }
          // ⚠ WORKAROUND pending Sprinklr's deterministic hand-off, kept isolated to delete in
          // one move: stay alive so the survey MESSAGE_RECEIVED event can still arrive.
          endSession({ awaitSurvey: true });
          surveyTimeout = setTimeout(() => {
            surveyTimeout = null;
            logger.log('[Sprinklr] No survey received within 30s — performing deferred cleanup');
            endSession();
          }, 30000);
        },
        onSurveyReceived: (surveyUrl) => {
          // Full cleanup — unsubscribes and destroys the SDK (deferred from the close above).
          // endSession also cancels the 30s fallback timer.
          endSession();
          const { onSurveyReceived } = getContext();
          if (onSurveyReceived) {
            onSurveyReceived(surveyUrl);
          }
        },
        onAgentTyping: (isTyping, { conversationId: typingConversationId }) => {
          logger.log('[Sprinklr] Agent typing:', isTyping, typingConversationId);
          if (typingConversationId && typingConversationId !== conversationId) return;
          clearTypingTimeout();
          setAgentTyping(isTyping);
          if (!isTyping) return;
          typingTimeout = setTimeout(() => {
            typingTimeout = null;
            setAgentTyping(false);
          }, TYPING_TTL_MS);
        },
        onImageReceived: (imageData) => {
          const { onImageReceived } = getContext();
          if (onImageReceived) {
            onImageReceived(imageData);
          }
        },
      });

      setAgentConnected(true);
      hasFirstMessageBeenSent = false;
      setEscalationActive();
      // The one place that sees every route to a live agent (idempotent promotion).
      // See .claude/context/live-agent.md
      const { onAgentConnected } = getContext();
      if (onAgentConnected) {
        onAgentConnected();
      }
      emitLiveAgentAnalytics({
        lvQuestion: LIVE_AGENT_ANALYTICS_CONNECTION_STRING,
        sessionId: getContext().sessionId,
        conversationId,
        caseNumber,
      });

      logger.log('[Sprinklr] initiateAgentChat — complete. isAgentConnected=true');
    };

    try {
      await attempt();
    } catch (firstErr) {
      logger.error('[sprinklrEngine] Agent chat init failed; retrying once, clean:', firstErr);
      // Clear and retry with a NEW conversation rather than the same (possibly dead) id.
      // See .claude/context/live-agent.md
      setAgentConnected(false);
      clearEscalation();
      try {
        await attempt();
      } catch (secondErr) {
        // Both attempts failed: clean up and REJECT (no "ended" message — nothing connected).
        endSession();
        throw secondErr;
      }
    }
  };

  /**
   * Connects when `isEscalated` says messages must go to Sprinklr but no conversation is live.
   * Callers never check connection state themselves. See .claude/context/live-agent.md
   */
  const ensureConnected = async () => {
    if (!getContext().enabled || isAgentConnected) return;
    await initiateAgentChat();
  };

  /**
   * Sends a user message through the Sprinklr conversation channel.
   */
  const sendAgentMessage = async (text, actionId) => {
    logger.log('[Sprinklr] sendAgentMessage:', text);
    const isFirstLCMessage = !hasFirstMessageBeenSent;
    hasFirstMessageBeenSent = true;
    const suffix = isFirstLCMessage ? LIVE_AGENT_ANALYTICS_FIRST_MESSAGE_SUFFIX : '';
    emitLiveAgentAnalytics({
      lvQuestion: `${LIVE_AGENT_ANALYTICS_CUSTOM_MESSAGE_PREFIX}${text}${suffix}`,
      sessionId: getContext().sessionId,
      actionId,
      conversationId,
      caseNumber,
    });
    await ensureConnected();
    try {
      await sendMessage(text, conversationId, actionId);
    } catch (err) {
      logger.error('[sprinklrEngine] Failed to send message:', err);
      // Recovery per "get them back to a human however possible": reconnect and redeliver.
      // See .claude/context/live-agent.md
      clearEscalation();
      await initiateAgentChat({ text, actionId });
    }
  };

  /**
   * Sends a user image message through the Sprinklr conversation channel.
   */
  const sendAgentImage = async (text, file, actionId) => {
    logger.log('[Sprinklr] sendAgentImage:', file?.name);
    await ensureConnected();
    try {
      await sendMessageWithImage(text, file, conversationId, actionId);
    } catch (err) {
      logger.error('[sprinklrEngine] Failed to send agent image:', err);
      // Same recovery as sendAgentMessage, but reconnect+resend the IMAGE, not the last text.
      // See .claude/context/live-agent.md
      clearEscalation();
      await initiateAgentChat();
      await sendMessageWithImage(text, file, conversationId, actionId);
    }
  };

  /**
   * Handles idle TTL expiry: ends the session and broadcasts `MAA_IDLE_DISCONNECT` to other tabs.
   * Saves no message; the consumer's `onIdleDisconnect` writes the distinct idle wording.
   */
  const handleIdleDisconnect = async () => {
    await endSession();
    if (channel) {
      channel.postMessage({ type: 'MAA_IDLE_DISCONNECT' });
    }
  };

  /**
   * Cross-tab escalation sync. The conversation is intentionally live in EVERY tab so the
   * user can move between them mid-chat.
   */
  const handleBroadcast = ({
    data: {
      type,
      conversationId: newConversationId,
      caseNumber: newCaseNumber,
      lastActivityAt,
    }
  }) => {
    if (type === 'MAA_ESCALATE_TO_AGENT') {
      setEscalationActive();
      if (newConversationId) {
        conversationId = newConversationId;
        caseNumber = newCaseNumber ?? null;
        lastConversationId = newConversationId;
        lastCaseNumber = newCaseNumber ?? null;
        writeSession({ conversationId: newConversationId, caseNumber: newCaseNumber });
      }
      initiateAgentChat().catch(() => {});
    }
    if (type === 'MAA_IDLE_DISCONNECT') {
      endSession();
    }
    // A peer ended the conversation. No message is written here — whoever DETECTED it did.
    if (type === 'MAA_AGENT_DISCONNECTED') {
      endSession({ awaitSurvey: true });
      surveyTimeout = setTimeout(() => {
        surveyTimeout = null;
        logger.log('[Sprinklr] No survey received within 30s — performing deferred cleanup');
        endSession();
      }, 30000);
    }
    if (type === 'MAA_UPDATE_LAST_ACTIVITY' && lastActivityAt) {
      writeSession({ lastActivityAt });
    }
  };

  /**
   * Reconnect-on-reload: a persisted escalation with a conversation id means the user was
   * mid-chat when the page went away. This is a REQUIREMENT, not an optimisation.
   */
  const resumeConversation = async () => {
    await initiateAgentChat().catch(() => {});

    const recentMessageResponse = await getMessages(
      { conversationId, limit: 10 }
    ).catch(() => ({ messages: [], closed: true }));
    const { messages: recentMessages, closed } = recentMessageResponse;
    if (closed) {
      // This tab DETECTED the end, so it writes the message, then cleans up.
      // See .claude/context/live-agent.md
      clearEscalation();
      const { onConversationEnd } = getContext();
      if (onConversationEnd) {
        onConversationEnd({ messageId: conversationId });
      }
      endSession();
      return;
    }

    recentMessages.forEach((msg) => {
      const { onAgentMessage } = getContext();
      if (onAgentMessage) {
        onAgentMessage(msg).catch(() => {});
      }
    });
  };

  /**
   * Starts the block: hydrates intent, opens the cross-tab channel, subscribes to the host bus,
   * then reconciles. Each step runs once. See .claude/context/live-agent.md
   */
  const init = () => {
    const { enabled } = getContext();
    // Journeys without live agent never hydrate escalation — the store stays false, and
    // every step below is skipped.
    if (!enabled) return;

    // Hydrate store.isEscalated from the persisted record once; transitions write through
    // publishEscalation thereafter.
    publishEscalation(persistence.read()?.isEscalated ?? false);

    if (typeof BroadcastChannel !== 'undefined') {
      channel = createBroadcastChannel(CHANNELS.assistant);
      channel.onmessage = handleBroadcast;
    }

    // Defensively clear any handler left by a prior instance before registering this one.
    unsubscribeAll('magic-apron-assistant.close-agent-conversation');
    unsubscribeBus = subscribe('magic-apron-assistant.close-agent-conversation', closeAgentConversation);

    // EXCLUSIVE, and idle FIRST: an expired record must never start a connect, and expiry
    // must be read before anything can refresh lastActivityAt.
    const record = persistence.read() || {};
    if (checkIdleExpired()) {
      handleIdleDisconnect().catch(() => {});
      const { onIdleDisconnect } = getContext();
      if (onIdleDisconnect) {
        onIdleDisconnect();
      }
    } else if (record.isEscalated && record.conversationId) {
      resumeConversation();
    }
  };

  /**
   * Stops the block. It does NOT destroy the SDK: closing the drawer does not end a conversation.
   * See .claude/context/live-agent.md
   */
  const dispose = () => {
    if (channel) {
      channel.close();
      channel = null;
    }
    if (unsubscribeBus) {
      unsubscribeBus();
      unsubscribeBus = null;
    }
    if (unsubscribeMessages) {
      unsubscribeMessages();
      unsubscribeMessages = null;
    }
    // ⚠ Must read the instance field, not React state — see .claude/context/live-agent.md
    // ("dispose() reads the engine's isAgentConnected instance field").
    if (isAgentConnected) {
      emitLiveAgentAnalytics({
        lvQuestion: LIVE_AGENT_ANALYTICS_DISCONNECTION_STRING,
        sessionId: getContext().sessionId,
        conversationId: lastConversationId,
        caseNumber: lastCaseNumber,
      });
    }
  };

  return {
    init,
    dispose,
    checkIdleExpired,
    initiateAgentChat,
    sendAgentMessage,
    sendAgentImage,
    clearEscalation,
    updateLastActivity,
    handleIdleDisconnect,
    getConversationIds,
  };
};
